import { Box, DateRangePicker, Select, TextField } from 'vopay';
import type { Timestamp } from 'vopay';
import { Formik } from 'formik';
import { subWeeks } from 'date-fns';
import { Button } from 'UI';
import { useTranslation } from 'react-i18next';

export type ReconciliationFilters = {
  timeRange: [Timestamp, Timestamp];
  token: string;
  accountName: string;
  isReconciled: string;
};

type Props = {
  initialValues: ReconciliationFilters;
  masterAccountOptions: Array<{ label: string; value: string }>;
  onSearch: (values: ReconciliationFilters) => void;
};

const defaultTimeRange: [Timestamp, Timestamp] = [
  subWeeks(new Date(), 1).getTime() as Timestamp,
  new Date().getTime() as Timestamp,
];

const reconciledOptions = [
  { label: 'All', value: '' },
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];

const ReconciliationSearchForm = ({ initialValues, masterAccountOptions, onSearch }: Props): React.JSX.Element => {
  const { t } = useTranslation('translations', { keyPrefix: 'Reconciliation.ReconciliationPage.SearchForm' });

  return (
    <Box ai="center">
      <Formik initialValues={initialValues} onSubmit={onSearch} enableReinitialize>
        {({ values, setFieldValue, handleChange, handleBlur, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <Box flx="row" height="auto" gap="sm" wrap="wrap">
              <Box height="auto">
                <DateRangePicker
                  onChange={(range) => setFieldValue('timeRange', range ?? defaultTimeRange)}
                  initialTime={values.timeRange}
                  triggerVariant="outlineShadow"
                />
              </Box>
              {masterAccountOptions.length > 1 && (
                <Box mb="sm" height="auto" width="200px">
                  <Select
                    version="2"
                    placeholder={t('masterAccountPlaceholder', { defaultValue: 'Master Account' })}
                    options={masterAccountOptions}
                    name="token"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.token}
                    sort={false}
                  />
                </Box>
              )}

              <Box mb="sm" height="auto" width="200px">
                <Select
                  version="2"
                  placeholder={t('reconciledPlaceholder', { defaultValue: 'Reconciled' })}
                  options={reconciledOptions}
                  name="isReconciled"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.isReconciled}
                  sort={false}
                />
              </Box>
              <Box mb="sm" height="auto" width="200px">
                <TextField
                  name="accountName"
                  placeholder={t('accountNamePlaceholder', { defaultValue: 'Account Name' })}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.accountName}
                  variant="outlineShadow"
                />
              </Box>
              <Box jc="fe" height="auto" gap="sm">
                <Button id="reconciliation-search" colorScheme="primary" size="small" type="submit">
                  {t('searchButton', { defaultValue: 'Search' })}
                </Button>
              </Box>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default ReconciliationSearchForm;
